import React, { createContext, useContext, useReducer, ReactNode, useCallback } from 'react';
import { facebookAPI } from '../services/api';
import { useAuth } from './AuthContext';

// Types
export interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor?: string | string[];
    borderColor?: string | string[];
    borderWidth?: number;
    tension?: number;
    pointBackgroundColor?: string;
    pointBorderColor?: string;
    pointRadius?: number;
    pointHoverRadius?: number;
  }[];
}

export interface AIInsight {
  type: 'insight' | 'prediction' | 'warning' | 'recommendation';
  title: string;
  message: string;
  icon: string;
  color: string;
}

export interface AIAnalysis {
  insights: AIInsight[];
}

export interface ProblemData {
  title: string;
  count: number;
  recommendation: string;
}

export interface AccountData {
  pageName: string;
  username: string;
  followers: number;
  likes: number;
  posts: number;
  profilePicture: string;
  bio: string;
  website: string;
  isVerified: boolean;
  category: string;
}

export interface PostData {
  id: number | string;
  image: string;
  videoUrl?: string;
  caption: string;
  likes: number;
  comments: number;
  shares: number;
  reach: number;
  engagement: number;
  postedAt: string;
  type: string;
}

export interface FacebookState {
  account: AccountData | null;
  recentPosts: PostData[];
  topContent: PostData[];
  engagement: ChartData | null;
  reach: ChartData | null;
  contentPerformance: ChartData | null;
  hashtags: ChartData | null;
  sentiment: ChartData | null;
  problems: ProblemData[];
  aiAnalysis: {
    [key: string]: AIAnalysis | null;
  };
  loading: {
    account: boolean;
    recentPosts: boolean;
    topContent: boolean;
    engagement: boolean;
    reach: boolean;
    contentPerformance: boolean;
    hashtags: boolean;
    sentiment: boolean;
    problems: boolean;
    aiAnalysis: {
      [key: string]: boolean;
    };
  };
  error: {
    account: string | null;
    recentPosts: string | null;
    topContent: string | null;
    engagement: string | null;
    reach: string | null;
    contentPerformance: string | null;
    hashtags: string | null;
    sentiment: string | null;
    problems: string | null;
    aiAnalysis: {
      [key: string]: string | null;
    };
  };
}

export type FacebookAction =
  | { type: 'FETCH_ACCOUNT_START' }
  | { type: 'FETCH_ACCOUNT_SUCCESS'; payload: AccountData }
  | { type: 'FETCH_ACCOUNT_ERROR'; payload: string }
  | { type: 'FETCH_RECENT_POSTS_START' }
  | { type: 'FETCH_RECENT_POSTS_SUCCESS'; payload: PostData[] }
  | { type: 'FETCH_RECENT_POSTS_ERROR'; payload: string }
  | { type: 'FETCH_TOP_CONTENT_START' }
  | { type: 'FETCH_TOP_CONTENT_SUCCESS'; payload: PostData[] }
  | { type: 'FETCH_TOP_CONTENT_ERROR'; payload: string }
  | { type: 'FETCH_ENGAGEMENT_START' }
  | { type: 'FETCH_ENGAGEMENT_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_ENGAGEMENT_ERROR'; payload: string }
  | { type: 'FETCH_REACH_START' }
  | { type: 'FETCH_REACH_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_REACH_ERROR'; payload: string }
  | { type: 'FETCH_CONTENT_PERFORMANCE_START' }
  | { type: 'FETCH_CONTENT_PERFORMANCE_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_CONTENT_PERFORMANCE_ERROR'; payload: string }
  | { type: 'FETCH_HASHTAGS_START' }
  | { type: 'FETCH_HASHTAGS_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_HASHTAGS_ERROR'; payload: string }
  | { type: 'FETCH_SENTIMENT_START' }
  | { type: 'FETCH_SENTIMENT_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_SENTIMENT_ERROR'; payload: string }
  | { type: 'FETCH_PROBLEMS_START' }
  | { type: 'FETCH_PROBLEMS_SUCCESS'; payload: ProblemData[] }
  | { type: 'FETCH_PROBLEMS_ERROR'; payload: string }
  | { type: 'FETCH_AI_ANALYSIS_START'; payload: string }
  | { type: 'FETCH_AI_ANALYSIS_SUCCESS'; payload: { chartType: string; data: AIAnalysis } }
  | { type: 'FETCH_AI_ANALYSIS_ERROR'; payload: { chartType: string; error: string } };

const initialState: FacebookState = {
  account: null,
  recentPosts: [],
  topContent: [],
  engagement: null,
  reach: null,
  contentPerformance: null,
  hashtags: null,
  sentiment: null,
  problems: [],
  aiAnalysis: {},
  loading: {
    account: false,
    recentPosts: false,
    topContent: false,
    engagement: false,
    reach: false,
    contentPerformance: false,
    hashtags: false,
    sentiment: false,
    problems: false,
    aiAnalysis: {},
  },
  error: {
    account: null,
    recentPosts: null,
    topContent: null,
    engagement: null,
    reach: null,
    contentPerformance: null,
    hashtags: null,
    sentiment: null,
    problems: null,
    aiAnalysis: {},
  },
};

const facebookReducer = (state: FacebookState, action: FacebookAction): FacebookState => {
  switch (action.type) {
    case 'FETCH_ACCOUNT_START':
      return {
        ...state,
        loading: { ...state.loading, account: true },
        error: { ...state.error, account: null },
      };
    case 'FETCH_ACCOUNT_SUCCESS':
      return {
        ...state,
        account: action.payload,
        loading: { ...state.loading, account: false },
      };
    case 'FETCH_ACCOUNT_ERROR':
      return {
        ...state,
        loading: { ...state.loading, account: false },
        error: { ...state.error, account: action.payload },
      };
    case 'FETCH_RECENT_POSTS_START':
      return {
        ...state,
        loading: { ...state.loading, recentPosts: true },
        error: { ...state.error, recentPosts: null },
      };
    case 'FETCH_RECENT_POSTS_SUCCESS':
      return {
        ...state,
        recentPosts: action.payload,
        loading: { ...state.loading, recentPosts: false },
      };
    case 'FETCH_RECENT_POSTS_ERROR':
      return {
        ...state,
        loading: { ...state.loading, recentPosts: false },
        error: { ...state.error, recentPosts: action.payload },
      };
    case 'FETCH_TOP_CONTENT_START':
      return {
        ...state,
        loading: { ...state.loading, topContent: true },
        error: { ...state.error, topContent: null },
      };
    case 'FETCH_TOP_CONTENT_SUCCESS':
      return {
        ...state,
        topContent: action.payload,
        loading: { ...state.loading, topContent: false },
      };
    case 'FETCH_TOP_CONTENT_ERROR':
      return {
        ...state,
        loading: { ...state.loading, topContent: false },
        error: { ...state.error, topContent: action.payload },
      };
    case 'FETCH_ENGAGEMENT_START':
      return {
        ...state,
        loading: { ...state.loading, engagement: true },
        error: { ...state.error, engagement: null },
      };
    case 'FETCH_ENGAGEMENT_SUCCESS':
      return {
        ...state,
        engagement: action.payload,
        loading: { ...state.loading, engagement: false },
      };
    case 'FETCH_ENGAGEMENT_ERROR':
      return {
        ...state,
        loading: { ...state.loading, engagement: false },
        error: { ...state.error, engagement: action.payload },
      };
    case 'FETCH_REACH_START':
      return {
        ...state,
        loading: { ...state.loading, reach: true },
        error: { ...state.error, reach: null },
      };
    case 'FETCH_REACH_SUCCESS':
      return {
        ...state,
        reach: action.payload,
        loading: { ...state.loading, reach: false },
      };
    case 'FETCH_REACH_ERROR':
      return {
        ...state,
        loading: { ...state.loading, reach: false },
        error: { ...state.error, reach: action.payload },
      };
    case 'FETCH_CONTENT_PERFORMANCE_START':
      return {
        ...state,
        loading: { ...state.loading, contentPerformance: true },
        error: { ...state.error, contentPerformance: null },
      };
    case 'FETCH_CONTENT_PERFORMANCE_SUCCESS':
      return {
        ...state,
        contentPerformance: action.payload,
        loading: { ...state.loading, contentPerformance: false },
      };
    case 'FETCH_CONTENT_PERFORMANCE_ERROR':
      return {
        ...state,
        loading: { ...state.loading, contentPerformance: false },
        error: { ...state.error, contentPerformance: action.payload },
      };
    case 'FETCH_HASHTAGS_START':
      return {
        ...state,
        loading: { ...state.loading, hashtags: true },
        error: { ...state.error, hashtags: null },
      };
    case 'FETCH_HASHTAGS_SUCCESS':
      return {
        ...state,
        hashtags: action.payload,
        loading: { ...state.loading, hashtags: false },
      };
    case 'FETCH_HASHTAGS_ERROR':
      return {
        ...state,
        loading: { ...state.loading, hashtags: false },
        error: { ...state.error, hashtags: action.payload },
      };
    case 'FETCH_SENTIMENT_START':
      return {
        ...state,
        loading: { ...state.loading, sentiment: true },
        error: { ...state.error, sentiment: null },
      };
    case 'FETCH_SENTIMENT_SUCCESS':
      return {
        ...state,
        sentiment: action.payload,
        loading: { ...state.loading, sentiment: false },
      };
    case 'FETCH_SENTIMENT_ERROR':
      return {
        ...state,
        loading: { ...state.loading, sentiment: false },
        error: { ...state.error, sentiment: action.payload },
      };
    case 'FETCH_PROBLEMS_START':
      return {
        ...state,
        loading: { ...state.loading, problems: true },
        error: { ...state.error, problems: null },
      };
    case 'FETCH_PROBLEMS_SUCCESS':
      return {
        ...state,
        problems: action.payload,
        loading: { ...state.loading, problems: false },
      };
    case 'FETCH_PROBLEMS_ERROR':
      return {
        ...state,
        loading: { ...state.loading, problems: false },
        error: { ...state.error, problems: action.payload },
      };
    case 'FETCH_AI_ANALYSIS_START':
      return {
        ...state,
        loading: {
          ...state.loading,
          aiAnalysis: {
            ...state.loading.aiAnalysis,
            [action.payload]: true,
          },
        },
        error: {
          ...state.error,
          aiAnalysis: {
            ...state.error.aiAnalysis,
            [action.payload]: null,
          },
        },
      };
    case 'FETCH_AI_ANALYSIS_SUCCESS':
      return {
        ...state,
        aiAnalysis: {
          ...state.aiAnalysis,
          [action.payload.chartType]: action.payload.data,
        },
        loading: {
          ...state.loading,
          aiAnalysis: {
            ...state.loading.aiAnalysis,
            [action.payload.chartType]: false,
          },
        },
      };
    case 'FETCH_AI_ANALYSIS_ERROR':
      return {
        ...state,
        loading: {
          ...state.loading,
          aiAnalysis: {
            ...state.loading.aiAnalysis,
            [action.payload.chartType]: false,
          },
        },
        error: {
          ...state.error,
          aiAnalysis: {
            ...state.error.aiAnalysis,
            [action.payload.chartType]: action.payload.error,
          },
        },
      };
    default:
      return state;
  }
};

interface FacebookContextType {
  state: FacebookState;
  fetchAccountData: () => Promise<void>;
  fetchRecentPosts: () => Promise<void>;
  fetchTopContent: () => Promise<void>;
  fetchEngagementData: () => Promise<void>;
  fetchReachData: () => Promise<void>;
  fetchContentPerformanceData: () => Promise<void>;
  fetchHashtagsData: () => Promise<void>;
  fetchSentimentData: () => Promise<void>;
  fetchProblemsData: () => Promise<void>;
  fetchAIAnalysis: (chartType: string) => Promise<void>;
}

const FacebookContext = createContext<FacebookContextType | undefined>(undefined);

export const FacebookProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(facebookReducer, initialState);
  const { business } = useAuth();

  const fetchAccountData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_ACCOUNT_START' });
    try {
      const response = await facebookAPI.getAccountData(business._id);
      const raw = response.data.data;
      const details = raw?.details || {};
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const toProxy = (url?: string) => (url ? `${baseUrl}/proxy-image?url=${encodeURIComponent(url)}` : '');
      
      // Map API response to AccountData interface
      const normalizedAccount: AccountData = {
        pageName: details.displayName ?? '',
        username: details.username ?? '',
        followers: Number(details.followerCount ?? 0),
        likes: Number(details.followerCount ?? 0), // Facebook'ta likes genelde followerCount ile aynı
        posts: 0, // API'de posts count yok, gerekirse başka bir endpoint'ten alınabilir
        profilePicture: toProxy(details.profileImageUrl ?? ''),
        bio: details.bio ?? '',
        website: details.website ?? '',
        isVerified: Boolean(details.isVerified ?? false),
        category: details.category ?? '',
      };
      
      dispatch({ type: 'FETCH_ACCOUNT_SUCCESS', payload: normalizedAccount });
    } catch (error) {
      dispatch({ type: 'FETCH_ACCOUNT_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchRecentPosts = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_RECENT_POSTS_START' });
    try {
      const response = await facebookAPI.getRecentPosts(business._id);
      const rawPosts = Array.isArray(response.data.data) ? response.data.data : [];
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const toProxy = (url?: string) => (url ? `${baseUrl}/proxy-image?url=${encodeURIComponent(url)}` : '');
      
      // Map API response to PostData interface
      const posts: PostData[] = rawPosts.map((post: any) => {
        // Get image from various possible fields
        const imageUrl = post.imageUrl || 
                        (Array.isArray(post.mediaUrls) && post.mediaUrls.length > 0 ? post.mediaUrls[0] : '') ||
                        post.videoPreviewUrl || 
                        '';
        
        // Get video URL if it exists
        const videoUrl = post.videoUrl || 
                        (post.type === 'video' && Array.isArray(post.mediaUrls) && post.mediaUrls.length > 0 ? post.mediaUrls[0] : '') ||
                        '';
        
        // Calculate reach (videoViewCount for videos, or 0)
        const reach = post.videoViewCount ?? post.videoPlayCount ?? 0;
        
        // Calculate engagement percentage
        const totalEngagement = (post.likes ?? 0) + (post.comments ?? 0) + (post.shares ?? 0);
        const engagement = reach > 0 ? Math.round((totalEngagement / reach) * 100 * 100) / 100 : 0;
        
        return {
          id: post.id || post._id || '',
          image: toProxy(imageUrl),
          videoUrl: videoUrl ? toProxy(videoUrl) : undefined,
          caption: post.text || post.caption || '',
          likes: post.likes ?? 0,
          comments: post.comments ?? 0,
          shares: post.shares ?? 0,
          reach: reach,
          engagement: engagement,
          postedAt: post.createdAt || post.postedAt || '',
          type: post.type || 'photo',
        };
      });
      
      dispatch({ type: 'FETCH_RECENT_POSTS_SUCCESS', payload: posts });
    } catch (error) {
      dispatch({ type: 'FETCH_RECENT_POSTS_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchTopContent = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_TOP_CONTENT_START' });
    try {
      const response = await facebookAPI.getTopContent(business._id);
      const rawPosts = Array.isArray(response.data.data) ? response.data.data : [];
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const toProxy = (url?: string) => (url ? `${baseUrl}/proxy-image?url=${encodeURIComponent(url)}` : '');
      
      // Map API response to PostData interface
      const posts: PostData[] = rawPosts.map((post: any) => {
        // Get image from various possible fields
        const imageUrl = post.imageUrl || 
                        (Array.isArray(post.mediaUrls) && post.mediaUrls.length > 0 ? post.mediaUrls[0] : '') ||
                        post.videoPreviewUrl || 
                        '';
        
        // Get video URL if it exists
        const videoUrl = post.videoUrl || 
                        (post.type === 'video' && Array.isArray(post.mediaUrls) && post.mediaUrls.length > 0 ? post.mediaUrls[0] : '') ||
                        '';
        
        // Calculate reach (videoViewCount for videos, or 0)
        const reach = post.videoViewCount ?? post.videoPlayCount ?? 0;
        
        // Calculate engagement percentage
        const totalEngagement = (post.likes ?? 0) + (post.comments ?? 0) + (post.shares ?? 0);
        const engagement = reach > 0 ? Math.round((totalEngagement / reach) * 100 * 100) / 100 : 0;
        
        return {
          id: post.id || post._id || '',
          image: toProxy(imageUrl),
          videoUrl: videoUrl ? toProxy(videoUrl) : undefined,
          caption: post.text || post.caption || '',
          likes: post.likes ?? 0,
          comments: post.comments ?? 0,
          shares: post.shares ?? 0,
          reach: reach,
          engagement: engagement,
          postedAt: post.createdAt || post.postedAt || '',
          type: post.type || 'photo',
        };
      });
      
      dispatch({ type: 'FETCH_TOP_CONTENT_SUCCESS', payload: posts });
    } catch (error) {
      dispatch({ type: 'FETCH_TOP_CONTENT_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchEngagementData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_ENGAGEMENT_START' });
    try {
      const response = await facebookAPI.getEngagementData(business._id);
      dispatch({ type: 'FETCH_ENGAGEMENT_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ type: 'FETCH_ENGAGEMENT_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchReachData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_REACH_START' });
    try {
      const response = await facebookAPI.getReachData(business._id);
      dispatch({ type: 'FETCH_REACH_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ type: 'FETCH_REACH_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchContentPerformanceData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_CONTENT_PERFORMANCE_START' });
    try {
      const response = await facebookAPI.getContentPerformanceData(business._id);
      dispatch({ type: 'FETCH_CONTENT_PERFORMANCE_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ type: 'FETCH_CONTENT_PERFORMANCE_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchHashtagsData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_HASHTAGS_START' });
    try {
      const response = await facebookAPI.getHashtagsData(business._id);
      dispatch({ type: 'FETCH_HASHTAGS_SUCCESS', payload: response.data.data });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({ type: 'FETCH_HASHTAGS_ERROR', payload: 'PREPARING' });
      } else {
        dispatch({ type: 'FETCH_HASHTAGS_ERROR', payload: 'Veri yüklenirken hata oluştu' });
      }
    }
  }, [business?._id]);

  const fetchSentimentData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_SENTIMENT_START' });
    try {
      const response = await facebookAPI.getSentimentData(business._id);
      dispatch({ type: 'FETCH_SENTIMENT_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ type: 'FETCH_SENTIMENT_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchAIAnalysis = useCallback(async (chartType: string) => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: chartType });
    try {
      const response = await facebookAPI.getAIAnalysis(business._id, chartType);
      dispatch({
        type: 'FETCH_AI_ANALYSIS_SUCCESS',
        payload: { chartType, data: response.data.data },
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({
          type: 'FETCH_AI_ANALYSIS_ERROR',
          payload: { chartType, error: 'PREPARING' },
        });
      } else {
        dispatch({
          type: 'FETCH_AI_ANALYSIS_ERROR',
          payload: { chartType, error: 'AI analizi yüklenirken hata oluştu' },
        });
      }
    }
  }, [business?._id]);

  const fetchProblemsData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_PROBLEMS_START' });
    try {
      const response = await facebookAPI.getProblemsData(business._id);
      dispatch({ type: 'FETCH_PROBLEMS_SUCCESS', payload: response.data.data });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({ type: 'FETCH_PROBLEMS_ERROR', payload: 'PREPARING' });
      } else {
        dispatch({ type: 'FETCH_PROBLEMS_ERROR', payload: 'Problem verileri yüklenirken hata oluştu' });
      }
    }
  }, [business?._id]);

  return (
    <FacebookContext.Provider
      value={{
        state,
        fetchAccountData,
        fetchRecentPosts,
        fetchTopContent,
        fetchEngagementData,
        fetchReachData,
        fetchContentPerformanceData,
        fetchHashtagsData,
        fetchSentimentData,
        fetchProblemsData,
        fetchAIAnalysis,
      }}
    >
      {children}
    </FacebookContext.Provider>
  );
};

export const useFacebook = (): FacebookContextType => {
  const context = useContext(FacebookContext);
  if (context === undefined) {
    throw new Error('useFacebook must be used within a FacebookProvider');
  }
  return context;
};
